import React, { useCallback, useState } from 'react';
import { Copy, Loader2, Phone, Video, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import './CommentThreadMeetingChip.css';

interface CommentThreadMeetingChipProps {
  active: boolean;
  meetUrl: string | null;
  hostName: string;
  isHost: boolean;
  loading: boolean;
  error: string | null;
  calendarConnected: boolean | null;
  dialInPin?: string;
  onStart: () => void | Promise<void>;
  onEnd: () => void | Promise<void>;
  /** @deprecated use layout */
  compact?: boolean;
  /** chip = standalone row; composerIcon = attach-style icon; composerBanner = active strip above composer */
  layout?: 'chip' | 'composerIcon' | 'composerBanner';
  disabled?: boolean;
}

const CommentThreadMeetingChip: React.FC<CommentThreadMeetingChipProps> = ({
  active,
  meetUrl,
  hostName,
  isHost,
  loading,
  error,
  calendarConnected,
  dialInPin,
  onStart,
  onEnd,
  compact,
  layout: layoutProp,
  disabled,
}) => {
  const layout = layoutProp ?? 'chip';
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(async () => {
    if (!meetUrl) return;
    try {
      await navigator.clipboard.writeText(meetUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [meetUrl]);

  const join = useCallback(() => {
    if (!meetUrl) return;
    window.open(meetUrl, '_blank', 'noopener,noreferrer');
  }, [meetUrl]);

  if (layout === 'composerBanner' && (!active || !meetUrl)) {
    return null;
  }

  if (layout === 'composerIcon') {
    if (active && meetUrl) {
      return (
        <button
          type="button"
          className="tc-btn-attach ctm-composer-join"
          onClick={join}
          title="Join quick meeting"
          aria-label="Join quick meeting"
          disabled={loading || disabled}
        >
          <Video size={14} strokeWidth={2.25} />
        </button>
      );
    }
    return (
      <>
        <button
          type="button"
          className="tc-btn-attach"
          onClick={() => void onStart()}
          disabled={loading || disabled || calendarConnected === false}
          title={
            calendarConnected === false
              ? 'Connect Google Calendar on the Calendar page to start a Meet'
              : 'Start a quick Google Meet for this thread'
          }
          aria-label="Quick meeting"
        >
          {loading ? (
            <Loader2 size={14} className="spinning" aria-hidden />
          ) : (
            <Video size={14} strokeWidth={2.25} aria-hidden />
          )}
        </button>
        {error ? <span className="ctm-error ctm-error--composer" role="alert">{error}</span> : null}
      </>
    );
  }

  if (active && meetUrl) {
    return (
      <div
        className={`ctm-banner${compact ? ' ctm-banner--compact' : ''}${layout === 'composerBanner' ? ' ctm-banner--composer' : ''}`}
        role="status"
      >
        <div className="ctm-banner-main">
          <Video size={14} strokeWidth={2.25} aria-hidden />
          <span>
            <strong>{hostName.split(' ')[0] || hostName}</strong> started a quick meeting
          </span>
          <button type="button" className="ctm-btn ctm-btn--primary" onClick={join} disabled={loading}>
            Join
          </button>
          <button type="button" className="ctm-btn ctm-btn--ghost" onClick={() => void copyLink()} title="Copy Meet link">
            <Copy size={13} aria-hidden />
            {copied ? 'Copied' : 'Copy'}
          </button>
          {dialInPin ? (
            <span className="ctm-pin" title="Phone PIN">
              <Phone size={12} aria-hidden />
              {dialInPin}
            </span>
          ) : null}
        </div>
        {isHost ? (
          <button
            type="button"
            className="ctm-btn ctm-btn--end"
            onClick={() => void onEnd()}
            disabled={loading}
            aria-label="End meeting for everyone in this thread"
          >
            {loading ? <Loader2 size={13} className="spinning" aria-hidden /> : <X size={13} aria-hidden />}
            End
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`ctm-start-wrap${compact ? ' ctm-start-wrap--compact' : ''}`}>
      <button
        type="button"
        className="ctm-btn ctm-btn--start"
        onClick={() => void onStart()}
        disabled={loading || calendarConnected === false}
        title={
          calendarConnected === false
            ? 'Connect Google Calendar to create a Meet link'
            : 'Start a Google Meet for everyone in this thread'
        }
      >
        {loading ? (
          <Loader2 size={13} className="spinning" aria-hidden />
        ) : (
          <Video size={13} strokeWidth={2.25} aria-hidden />
        )}
        Quick meeting
      </button>
      {calendarConnected === false ? (
        <span className="ctm-connect-hint">
          <Link to="/calendar">Connect Google Calendar</Link> to start a Meet.
        </span>
      ) : null}
      {error ? <span className="ctm-error" role="alert">{error}</span> : null}
    </div>
  );
};

export default CommentThreadMeetingChip;
